import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'
import {useMemo, useRef} from 'react'

import {useMatrixParamBuildContext} from '../contexts/MatrixParamBuildContext'
import {useMatrixParamBuildError} from '../contexts/MatrixParamBuildErrorContext'
import type {MatrixParamBuildValues} from '../MatrixParamBuild.types'

function useMatrixParamBuildParameterValues(
  parameterIndex: number,
  values: MatrixParamBuildValues[],
  options?: SelectItem[],
) {
  const latestInputValueRef = useRef<HTMLInputElement>(null)
  const {deleteErrorMessageHandler} = useMatrixParamBuildError()
  const {addParameterValueHandler, deleteParameterValueHandler} = useMatrixParamBuildContext()
  const parameterValues = useMemo(
    () =>
      values.map(({label, value}) => {
        const itemAlias = label ? `${label}=>` : ''

        return `${itemAlias}${value}`
      }),
    [values],
  )
  const parameterValuesSet = useMemo(() => new Set(parameterValues), [parameterValues])
  const valueSelectOptions = useMemo(
    () => options?.filter(({key}) => !parameterValuesSet.has(key.toString())),
    [options, parameterValuesSet],
  )
  const deleteParameterValueButtonClickHandler = (index: number) => () => {
    deleteErrorMessageHandler(parameterIndex)
    deleteParameterValueHandler(parameterIndex, index)
  }
  const addParameterValueButtonClickHandler = () => {
    addParameterValueHandler(parameterIndex)
    setTimeout(() => latestInputValueRef?.current?.focus(), 1)
  }
  const valueDuplicates = useMemo(
    () =>
      parameterValues.reduce<Map<string, number>>((accumulator, value) => {
        let amount = accumulator.get(value)

        if (value) {
          accumulator.set(value, typeof amount === 'number' ? ++amount : 0)
        }

        return accumulator
      }, new Map()),
    [parameterValues],
  )
  const isAllowDelete = values.length > 1

  return {
    isAllowDelete,
    parameterValues,
    valueSelectOptions,
    valueDuplicates,
    latestInputValueRef,
    deleteParameterValueButtonClickHandler,
    addParameterValueButtonClickHandler,
  }
}

export default useMatrixParamBuildParameterValues
